import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom'
import { useFetchProductSettings } from "../hooks/useFetchProductSettings";
import { useFetchProductSettingsDistributorOverrides } from "../hooks/useFetchProductSettingsDistributorOverrides";
import { useFetchProductSettingsCustomerPartNumbers } from "../hooks/useFetchProductSettingsCustomerPartNumbers";
import { useDistributorsContext } from "../../../Contexts/distributorsContext";
import { useFetchProductFulfillment } from "../hooks/useFetchProductFulfillment";
import { useFetchSkuFulfillmentTypesByCustomerType } from "../hooks/useFetchSkuFulfillmentTypesByCustomerType";
import { useFetchFulfillmentMethodMappings } from "../hooks/useFetchFulfillmentMethodMappings";
import {
    useFetchItemPropertyOverrides,
    PutItemPropertyOverride,
    DeleteItemPropertyOverride
} from "../hooks/useFetchItemPropertyOverrides";
import { GeographyLevel } from "../../../Enums/GeographyLevel";
import { toYyyyMmDdInput } from "../utils/inMarketDate";
import PropTypes from 'prop-types';

export const ProductSettingsContext = React.createContext(null);

ProductSettingsContextProvider.propTypes = {
    children: PropTypes.node,
}

export function ProductSettingsContextProvider({ children }) {
    const { sku } = useParams();
    const { distributors, isReady } = useDistributorsContext();
    const productDefaults = useFetchProductSettings(sku);
    const productSettingsDistributorOverrides = useFetchProductSettingsDistributorOverrides(sku);
    const customerPartNumbers = useFetchProductSettingsCustomerPartNumbers(sku);
    const productFulfillment = useFetchProductFulfillment(sku);
    const skuFulfillmentTypesByCustomerType = useFetchSkuFulfillmentTypesByCustomerType();
    const fulfillmentMethodMappings = useFetchFulfillmentMethodMappings();
    const fetchedItemPropertyOverrides = useFetchItemPropertyOverrides(sku);
    // Item-level (SKU default) in-market dates. Shared by every distributor row, so any row carries
    // them. They bound the geography overrides: a geo in-market date cannot start before the item's
    // in-market date, and a geo end date cannot run past the item's end in-market date.
    const itemInMarketDate = toYyyyMmDdInput(productFulfillment?.[0]?.inMarketDateDefault);
    const itemEndInMarketDate = toYyyyMmDdInput(productFulfillment?.[0]?.endInMarketDateDefault);
    const [itemPropertyOverrides, setItemPropertyOverrides] = useState({});
    const [itemPropertyEndInMarketOverrides, setItemPropertyEndInMarketOverrides] = useState({});
    const [dataLoaded, setDataLoaded] = useState(false);
    const [unfilteredProductSettingsDistributorTable, setUnfilteredProductSettingsDistributorTable] = useState([]);
    const [productSettingsDistributorTable, setProductSettingsDistributorTable] = useState();
    const [searchString, setSearchString] = useState("");
    // rows with selected checkbox
    const [selected, setSelected] = useState(new Set());
    // message shown in a pop-up when an invalid in-market date is entered
    const [inMarketDateError, setInMarketDateError] = useState(null);

    const showInMarketDateError = (message) => setInMarketDateError(message);
    const clearInMarketDateError = () => setInMarketDateError(null);

    const overrideKey = (salesGeographyLevelId, salesGeographyId) => `${salesGeographyLevelId}-${salesGeographyId}`;

    const getItemPropertyOverrideDate = (salesGeographyLevelId, salesGeographyId) => {
        if (salesGeographyLevelId == null || salesGeographyId == null) return '';
        return itemPropertyOverrides[overrideKey(salesGeographyLevelId, salesGeographyId)] ?? '';
    };

    const setItemPropertyOverrideDate = (salesGeographyLevelId, salesGeographyId, date) => {
        setItemPropertyOverrides(previous => {
            const updated = { ...previous };
            if (date) {
                updated[overrideKey(salesGeographyLevelId, salesGeographyId)] = date;
            } else {
                delete updated[overrideKey(salesGeographyLevelId, salesGeographyId)];
            }
            return updated;
        });
    };

    const getItemPropertyEndInMarketOverrideDate = (salesGeographyLevelId, salesGeographyId) => {
        if (salesGeographyLevelId == null || salesGeographyId == null) return '';
        return itemPropertyEndInMarketOverrides[overrideKey(salesGeographyLevelId, salesGeographyId)] ?? '';
    };

    const setItemPropertyEndInMarketOverrideDate = (salesGeographyLevelId, salesGeographyId, date) => {
        setItemPropertyEndInMarketOverrides(previous => {
            const updated = { ...previous };
            if (date) {
                updated[overrideKey(salesGeographyLevelId, salesGeographyId)] = date;
            } else {
                delete updated[overrideKey(salesGeographyLevelId, salesGeographyId)];
            }
            return updated;
        });
    };

    const saveItemPropertyOverride = async (groupingRow, date) => {
        const { salesGeographyLevelId, salesGeographyId, divisionId, regionId } = groupingRow;
        if (salesGeographyLevelId == null || salesGeographyId == null) return;

        if (date) {
            // The end date lives on the same row; preserve it when the in-market date changes.
            const endDate = getItemPropertyEndInMarketOverrideDate(salesGeographyLevelId, salesGeographyId) || null;
            const result = await PutItemPropertyOverride(sku, salesGeographyLevelId, salesGeographyId, date, divisionId, regionId, endDate);
            if (result.ok) {
                setItemPropertyOverrideDate(salesGeographyLevelId, salesGeographyId, date);
            } else {
                showInMarketDateError(result.error || 'The in-market date could not be saved.');
            }
        } else {
            const success = await DeleteItemPropertyOverride(sku, salesGeographyLevelId, salesGeographyId);
            if (success) {
                setItemPropertyOverrideDate(salesGeographyLevelId, salesGeographyId, null);
                // Clearing the in-market date removes the whole row, so its end date goes too.
                setItemPropertyEndInMarketOverrideDate(salesGeographyLevelId, salesGeographyId, null);
            }
        }
    };

    const saveItemPropertyEndInMarketOverride = async (groupingRow, endDate) => {
        const { salesGeographyLevelId, salesGeographyId, divisionId, regionId } = groupingRow;
        if (salesGeographyLevelId == null || salesGeographyId == null) return;

        // End date can only be set where an in-market date already exists (requires start).
        const inMarketDate = getItemPropertyOverrideDate(salesGeographyLevelId, salesGeographyId);
        if (!inMarketDate) return;

        const result = await PutItemPropertyOverride(sku, salesGeographyLevelId, salesGeographyId, inMarketDate, divisionId, regionId, endDate);
        if (result.ok) {
            setItemPropertyEndInMarketOverrideDate(salesGeographyLevelId, salesGeographyId, endDate);
        } else {
            showInMarketDateError(result.error || 'The end in-market date could not be saved.');
        }
    };

    const geographyDescendants = useMemo(() => {
        const divisionRegions = {};
        const divisionDistricts = {};
        const regionDistricts = {};
        const divisionDistributorMin = {};
        const regionDistributorMin = {};
        const districtDistributorMin = {};
        const divisionDistributorEndMin = {};
        const regionDistributorEndMin = {};
        const districtDistributorEndMin = {};

        const addTo = (map, key, value) => {
            if (key == null || value == null) return;
            (map[key] = map[key] ?? new Set()).add(value);
        };

        const considerMin = (map, key, dateStr) => {
            if (key == null || !dateStr) return;
            if (!map[key] || dateStr < map[key]) map[key] = dateStr;
        };

        for (const row of unfilteredProductSettingsDistributorTable) {
            if (row.kind && row.kind.value !== 3) continue;
            addTo(divisionRegions, row.divisionId, row.regionId);
            addTo(divisionDistricts, row.divisionId, row.districtId);
            addTo(regionDistricts, row.regionId, row.districtId);

            const override = toYyyyMmDdInput(row.inMarketDateOverride);
            if (override) {
                considerMin(divisionDistributorMin, row.divisionId, override);
                considerMin(regionDistributorMin, row.regionId, override);
                considerMin(districtDistributorMin, row.districtId, override);
            }

            const endOverride = toYyyyMmDdInput(row.endInMarketDateOverride);
            if (endOverride) {
                considerMin(divisionDistributorEndMin, row.divisionId, endOverride);
                considerMin(regionDistributorEndMin, row.regionId, endOverride);
                considerMin(districtDistributorEndMin, row.districtId, endOverride);
            }
        }

        return {
            divisionRegions, divisionDistricts, regionDistricts,
            divisionDistributorMin, regionDistributorMin, districtDistributorMin,
            divisionDistributorEndMin, regionDistributorEndMin, districtDistributorEndMin
        };
    }, [unfilteredProductSettingsDistributorTable]);

    const earliestSetDate = (salesGeographyLevelId, salesGeographyIds) => {
        let earliest = '';
        for (const salesGeographyId of salesGeographyIds ?? []) {
            const date = getItemPropertyOverrideDate(salesGeographyLevelId, salesGeographyId);
            if (date && (!earliest || date < earliest)) {
                earliest = date;
            }
        }
        return earliest;
    };

    const minNonEmpty = (...dates) => {
        let earliest = '';
        for (const date of dates) {
            if (date && (!earliest || date < earliest)) earliest = date;
        }
        return earliest;
    };

    const getFloorDate = (groupingRow) => {
        let ancestorFloor;
        switch (groupingRow.kind.value) {
            case 1:
                ancestorFloor = getItemPropertyOverrideDate(GeographyLevel.DIVISION, groupingRow.divisionId);
                break;
            case 2:
                ancestorFloor = getItemPropertyOverrideDate(GeographyLevel.REGION, groupingRow.regionId)
                    || getItemPropertyOverrideDate(GeographyLevel.DIVISION, groupingRow.divisionId);
                break;
            default:
                ancestorFloor = '';
        }
        // A geo in-market date cannot start before the item-level (SKU default) in-market date.
        return maxNonEmpty(itemInMarketDate, ancestorFloor);
    };

    // In-market ceiling: it cannot be later than the earliest in-market date already set below it, nor
    // later than the item-level (SKU default) end in-market date — a start cannot fall after the end.
    const getCeilingDate = (groupingRow) => {
        let descendantCeiling;
        switch (groupingRow.kind.value) {
            case 0:
                descendantCeiling = minNonEmpty(
                    earliestSetDate(GeographyLevel.REGION, geographyDescendants.divisionRegions[groupingRow.salesGeographyId]),
                    earliestSetDate(GeographyLevel.DISTRICT, geographyDescendants.divisionDistricts[groupingRow.salesGeographyId]),
                    geographyDescendants.divisionDistributorMin[groupingRow.salesGeographyId]);
                break;
            case 1:
                descendantCeiling = minNonEmpty(
                    earliestSetDate(GeographyLevel.DISTRICT, geographyDescendants.regionDistricts[groupingRow.salesGeographyId]),
                    geographyDescendants.regionDistributorMin[groupingRow.salesGeographyId]);
                break;
            case 2:
                descendantCeiling = geographyDescendants.districtDistributorMin[groupingRow.salesGeographyId] || '';
                break;
            default:
                descendantCeiling = '';
        }
        return minNonEmpty(itemEndInMarketDate, descendantCeiling);
    };

    const getDistributorFloorDate = (row) => {
        const geoFloor = getItemPropertyOverrideDate(GeographyLevel.DISTRICT, row.districtId)
            || getItemPropertyOverrideDate(GeographyLevel.REGION, row.regionId)
            || getItemPropertyOverrideDate(GeographyLevel.DIVISION, row.divisionId);
        // A distributor's in-market date cannot start before the item-level (SKU default) in-market date.
        return maxNonEmpty(itemInMarketDate, geoFloor);
    };

    const maxNonEmpty = (...dates) => {
        let latest = '';
        for (const date of dates) {
            if (date && (!latest || date > latest)) latest = date;
        }
        return latest;
    };

    const earliestSetEndDate = (salesGeographyLevelId, salesGeographyIds) => {
        let earliest = '';
        for (const salesGeographyId of salesGeographyIds ?? []) {
            const date = getItemPropertyEndInMarketOverrideDate(salesGeographyLevelId, salesGeographyId);
            if (date && (!earliest || date < earliest)) {
                earliest = date;
            }
        }
        return earliest;
    };

    // End date floor: it must be on/after this level's own in-market date and on/after
    // the end date set on the level directly above it.
    const getEndInMarketFloorDate = (groupingRow) => {
        const ownInMarket = getItemPropertyOverrideDate(groupingRow.salesGeographyLevelId, groupingRow.salesGeographyId);
        let ancestorEnd = '';
        switch (groupingRow.kind.value) {
            case 1:
                ancestorEnd = getItemPropertyEndInMarketOverrideDate(GeographyLevel.DIVISION, groupingRow.divisionId);
                break;
            case 2:
                ancestorEnd = getItemPropertyEndInMarketOverrideDate(GeographyLevel.REGION, groupingRow.regionId)
                    || getItemPropertyEndInMarketOverrideDate(GeographyLevel.DIVISION, groupingRow.divisionId);
                break;
            default:
                ancestorEnd = '';
        }
        return maxNonEmpty(ownInMarket, ancestorEnd);
    };

    // End date ceiling: it cannot be later than the earliest end date already set below it, nor later
    // than the item-level (SKU default) end in-market date.
    const getEndInMarketCeilingDate = (groupingRow) => {
        let descendantCeiling;
        switch (groupingRow.kind.value) {
            case 0:
                descendantCeiling = minNonEmpty(
                    earliestSetEndDate(GeographyLevel.REGION, geographyDescendants.divisionRegions[groupingRow.salesGeographyId]),
                    earliestSetEndDate(GeographyLevel.DISTRICT, geographyDescendants.divisionDistricts[groupingRow.salesGeographyId]),
                    geographyDescendants.divisionDistributorEndMin[groupingRow.salesGeographyId]);
                break;
            case 1:
                descendantCeiling = minNonEmpty(
                    earliestSetEndDate(GeographyLevel.DISTRICT, geographyDescendants.regionDistricts[groupingRow.salesGeographyId]),
                    geographyDescendants.regionDistributorEndMin[groupingRow.salesGeographyId]);
                break;
            case 2:
                descendantCeiling = geographyDescendants.districtDistributorEndMin[groupingRow.salesGeographyId] || '';
                break;
            default:
                descendantCeiling = '';
        }
        return minNonEmpty(itemEndInMarketDate, descendantCeiling);
    };

    const getDistributorEndInMarketFloorDate = (row) => {
        const ownInMarket = toYyyyMmDdInput(row.inMarketDateOverride);
        const geoEnd = getItemPropertyEndInMarketOverrideDate(GeographyLevel.DISTRICT, row.districtId)
            || getItemPropertyEndInMarketOverrideDate(GeographyLevel.REGION, row.regionId)
            || getItemPropertyEndInMarketOverrideDate(GeographyLevel.DIVISION, row.divisionId);
        return maxNonEmpty(ownInMarket, geoEnd);
    };

    const getPossibleSkuFulfillmentTypes = (customerType, offeringStatus) => {
        // Filter mappings by customer type and offering status
        const relevantMappings = fulfillmentMethodMappings.filter(mapping =>
            mapping.customerType === customerType && mapping.offeringStatus === offeringStatus
        );

        // Convert to the expected format
        const skuFulfillmentTypes = relevantMappings.map(mapping => ({
            id: mapping.fulfillmentType,
            description: mapping.fulfillmentTypeDescription,
            isDefault: mapping.isDefault
        }));

        const result = {
            customerType: customerType,
            skuFulfillmentTypes: skuFulfillmentTypes
        };

        return result;
    };

    const isRowCheckboxIndeterminate = (productSettingsRow) => {
        switch (productSettingsRow.kind.value) {
            case 0:
                return productSettingsDistributorTable.filter(row => selected.has(row.id)).some(row => row.division === productSettingsRow.id) && !selected.has(productSettingsRow?.id)
            case 1:
                return productSettingsDistributorTable.filter(row => selected.has(row.id)).some(row => row.region === productSettingsRow.id) && !selected.has(productSettingsRow?.id)
            case 2:
                return productSettingsDistributorTable.filter(row => selected.has(row.id)).some(row => row.district === productSettingsRow.id) && !selected.has(productSettingsRow?.id)
            default:
                return false;
        }
    }

    const isRowSelected = (productSettingsRow) => {
        return selected.has(productSettingsRow?.id) ?? false
    }

    const onRowSelectClick = (productSettingsRow, isChecked) => {
        let divisionRowIds = productSettingsDistributorTable.filter(row => (row.division === productSettingsRow.id || (row.division && row.division === productSettingsRow.division)) && row.legal !== false).map(row => row.id);
        let regionRowIds = productSettingsDistributorTable.filter(row => (row.region === productSettingsRow.id || (row.region && row.region === productSettingsRow.region)) && row.legal !== false).map(row => row.id);
        let districtRowIds = productSettingsDistributorTable.filter(row => (row.district === productSettingsRow.id || (row.district && row.district === productSettingsRow.district)) && row.legal !== false).map(row => row.id);

        switch (productSettingsRow.kind.value) {
            case 0: // division
                if (isChecked) {
                    let updated = new Set([...selected, productSettingsRow.id, ...divisionRowIds]);
                    setSelected(updated);
                } else {
                    selected.delete(productSettingsRow.id);
                    for (let index = 0; index < divisionRowIds.length; index++) {
                        selected.delete(divisionRowIds[index]);
                    }

                    for (let index = 0; index < regionRowIds.length; index++) {
                        selected.delete(regionRowIds[index]);
                    }

                    for (let index = 0; index < districtRowIds.length; index++) {
                        selected.delete(districtRowIds[index]);
                    }
                    setSelected(new Set([...selected]));
                }
                break;
            case 1: //region
                if (isChecked) {
                    let updated = new Set([...selected, productSettingsRow.id, ...regionRowIds]);

                    if (divisionRowIds.every(item => updated.has(item))) {
                        updated = updated.add(productSettingsRow.division);
                    }
                    setSelected(updated);
                } else {
                    selected.delete(productSettingsRow.id);
                    for (let index = 0; index < regionRowIds.length; index++) {
                        selected.delete(regionRowIds[index]);
                    }

                    for (let index = 0; index < districtRowIds.length; index++) {
                        selected.delete(districtRowIds[index]);
                    }
                    setSelected(new Set([...selected]));
                }
                break;
            case 2: //district
                if (isChecked) {
                    let updated = new Set([...selected, productSettingsRow.id, ...districtRowIds]);

                    if (regionRowIds.every(item => updated.has(item))) {
                        updated = updated.add(productSettingsRow.region);
                    }
                    if (divisionRowIds.every(item => updated.has(item))) {
                        updated = updated.add(productSettingsRow.division);
                    }
                    setSelected(updated);
                } else {
                    selected.delete(productSettingsRow.id);
                    for (let index = 0; index < districtRowIds.length; index++) {
                        selected.delete(districtRowIds[index]);
                    }
                    setSelected(new Set([...selected]));
                }
                break;
            default:
                if (isChecked) {
                    let updated = new Set([...selected, productSettingsRow.id]);

                    if (districtRowIds.every(item => updated.has(item))) {
                        updated = updated.add(productSettingsRow.district);
                    }
                    if (regionRowIds.every(item => updated.has(item))) {
                        updated = updated.add(productSettingsRow.region);
                    }
                    if (divisionRowIds.every(item => updated.has(item))) {
                        updated = updated.add(productSettingsRow.division);
                    }
                    setSelected(updated);
                } else {
                    selected.delete(productSettingsRow.id);
                    setSelected(new Set([...selected]));
                }
        }

    }

    const updateProductSettingsDistributorTable = async (updatedRows) => {
        const updatedTable = unfilteredProductSettingsDistributorTable.map((row) => {
            const updatedRow = updatedRows.find(updated => updated.id === row.id);
            if (updatedRow) {
                return updatedRow;
            }
            return row;
        });

        await setUnfilteredProductSettingsDistributorTable(updatedTable);

        const preparedTable = loadPreparedTable(updatedTable, searchString);
        setProductSettingsDistributorTable(preparedTable);
    }

    const isChanged = (newRow, oldRow) => {
        const changed = (oldRow.palletQuantity !== newRow.palletQuantity ||
            oldRow.palletDivisor !== newRow.palletDivisor ||
            oldRow.unitWeight !== newRow.unitWeight ||
            oldRow.customerPartNumber !== newRow.customerPartNumber ||
            oldRow.skuOfferingStatus?.id !== newRow.skuOfferingStatus?.id ||
            oldRow.skuFulfillmentType?.id !== newRow.skuFulfillmentType?.id ||
            oldRow.iIndirectDistributorId !== newRow.iIndirectDistributorId ||
            oldRow.inMarketDateOverride !== newRow.inMarketDateOverride ||
            oldRow.endInMarketDateOverride !== newRow.endInMarketDateOverride);
        return changed;
    };

    const loadDistributorTableSettings = (distributors, productSettingsDistributorOverrides, productFulfillment, customerPartNumbers) => {
        if (Object.keys(productDefaults).length === 0 || Object.keys(productSettingsDistributorOverrides).length === 0 || Object.keys(productFulfillment).length === 0 || !isReady) {
            return null;
        }

        const result = distributors.map(distributor => {
            let override = productSettingsDistributorOverrides.find(override => override.distributorId === distributor.distributorId);
            let fulfillment = productFulfillment.find(fulfillment => fulfillment.distributor.distributorId === distributor.distributorId);
            let partNumber = customerPartNumbers.find(partNumber => partNumber.distributorId === distributor.distributorId);

            return {
                id: distributor.distributorId.toString(), ...distributor,
                legal: override?.legal ?? true,
                palletQuantity: override?.palletQuantity,
                palletDivisor: override?.palletDivisor,
                unitWeight: override?.unitWeight,
                ...fulfillment,
                product: productFulfillment[0].product,
                customerPartNumber: partNumber?.customerPartNumber ?? '',
            }
        });

        return result;
    }

    const searchFilter = (productSettingsDistributorTable, searchString) => {
        if (!searchString || searchString?.trim() === '') {
            return productSettingsDistributorTable;
        }

        let search = searchString.toLowerCase().trim()
        return productSettingsDistributorTable.filter((distributorRow) =>
            distributorRow.name?.toLowerCase().includes(search)
            || distributorRow.sapId?.toLowerCase().includes(search)
            || (search === 'legal' && distributorRow.legal)
            || (search === 'illegal' && !distributorRow.legal)
            || distributorRow.division?.toLowerCase().includes(search)
            || distributorRow.region?.toLowerCase().includes(search)
            || distributorRow.district?.toLowerCase().includes(search)
            || (distributorRow.skuFulfillmentType?.description?.toLowerCase().includes(search) ?? 'not offered'.includes(search))
            || (distributorRow.skuOfferingStatus?.description?.toLowerCase().includes(search) ?? 'removed'.includes(search)));
    };

    const loadPreparedTable = (productSettingsMap, searchString) => {
        productSettingsMap = searchFilter(productSettingsMap, searchString);

        productSettingsMap = productSettingsMap.sort((a, b) => {
            return a.division.localeCompare(b.division) || a.region.localeCompare(b.region) || a.district.localeCompare(b.district) || a.sapId.localeCompare(b.sapId);
        });

        return addLocationHierarchy(productSettingsMap);
    }

    const addLocationHierarchy = (productSettingsMap) => {
        let oldDivision = null;
        let oldRegion = null;
        let oldDistrict = null;
        let tempProductSettingsDistributorTable = [];

        for (let row of productSettingsMap) {
            if (row.division !== oldDivision) {
                tempProductSettingsDistributorTable.push({
                    id: row.division, kind: { value: 0, label: "division" },
                    salesGeographyLevelId: GeographyLevel.DIVISION, salesGeographyId: row.divisionId, divisionId: row.divisionId
                });
                oldDivision = row.division;
            }
            if (row.region !== oldRegion) {
                tempProductSettingsDistributorTable.push({
                    id: row.region, kind: { value: 1, label: "region" }, division: oldDivision,
                    salesGeographyLevelId: GeographyLevel.REGION, salesGeographyId: row.regionId,
                    divisionId: row.divisionId, regionId: row.regionId
                });
                oldRegion = row.region;
            }
            if (row.district !== oldDistrict) {
                tempProductSettingsDistributorTable.push({
                    id: row.district, kind: { value: 2, label: "district" }, division: oldDivision, region: oldRegion,
                    salesGeographyLevelId: GeographyLevel.DISTRICT, salesGeographyId: row.districtId,
                    divisionId: row.divisionId, regionId: row.regionId, districtId: row.districtId
                });
                oldDistrict = row.district;
            }
            tempProductSettingsDistributorTable.push({
                ...row,
                palletQuantity: row.palletQuantity ?? "",
                palletDivisor: row.palletDivisor ?? "",
                unitWeight: row.unitWeight ?? "",
                customerPartNumber: row.customerPartNumber ?? "",
                kind: { value: 3, label: "distributor" }
            });
        }

        return tempProductSettingsDistributorTable
    }

    const selectAllLegalRows = () => {
        const allIds = productSettingsDistributorTable.filter(row => row.legal !== false).map(row => row.id);
        setSelected(new Set([...allIds]));
    }

    const removeSelectedRows = () => {
        setSelected(new Set());
    }

    useEffect(() => {
        const map = {};
        const endMap = {};
        for (const entry of fetchedItemPropertyOverrides) {
            map[overrideKey(entry.salesGeographyLevelId, entry.salesGeographyId)] = toYyyyMmDdInput(entry.inMarketDate);
            if (entry.endInMarketDate) {
                endMap[overrideKey(entry.salesGeographyLevelId, entry.salesGeographyId)] = toYyyyMmDdInput(entry.endInMarketDate);
            }
        }
        setItemPropertyOverrides(map);
        setItemPropertyEndInMarketOverrides(endMap);
    }, [fetchedItemPropertyOverrides]);

    useEffect(() => {
        if (unfilteredProductSettingsDistributorTable.length > 0) {
            const preparedTable = loadPreparedTable(unfilteredProductSettingsDistributorTable, searchString);
            setProductSettingsDistributorTable(preparedTable);
            setDataLoaded(true);
        } else {
            if (distributors && productSettingsDistributorOverrides.length > 0 && productFulfillment.length > 0 && fulfillmentMethodMappings.length > 0) {
                const table = loadDistributorTableSettings(distributors, productSettingsDistributorOverrides, productFulfillment, customerPartNumbers);
                setUnfilteredProductSettingsDistributorTable(table);
                const preparedTable = loadPreparedTable(table, searchString);
                setProductSettingsDistributorTable(preparedTable);
                setDataLoaded(true);
            } else {
                setDataLoaded(false);
            }
        }
    }, [distributors, productSettingsDistributorOverrides, productFulfillment, customerPartNumbers, unfilteredProductSettingsDistributorTable, searchString]);

    return (<ProductSettingsContext.Provider value={{
        dataLoaded,
        productDefaults,
        productSettingsDistributorTable,
        updateProductSettingsDistributorTable,
        skuFulfillmentTypesByCustomerType,
        getPossibleSkuFulfillmentTypes,
        fulfillmentMethodMappings,
        isChanged,
        searchString,
        setSearchString,
        onRowSelectClick,
        selected,
        isRowSelected,
        isRowCheckboxIndeterminate,
        selectAllLegalRows,
        removeSelectedRows,
        unfilteredProductSettingsDistributorTable,
        getItemPropertyOverrideDate,
        getFloorDate,
        getCeilingDate,
        getDistributorFloorDate,
        saveItemPropertyOverride,
        getItemPropertyEndInMarketOverrideDate,
        getEndInMarketFloorDate,
        getEndInMarketCeilingDate,
        getDistributorEndInMarketFloorDate,
        itemInMarketDate,
        itemEndInMarketDate,
        saveItemPropertyEndInMarketOverride,
        inMarketDateError,
        showInMarketDateError,
        clearInMarketDateError
    }}>{children}</ProductSettingsContext.Provider>)
}

export function useProductSettingsContext() {
    const context = useContext(ProductSettingsContext);

    if (!context) throw new Error("useProductSettingsContext must be used within a ProductSettingsContextProvider. Wrap a parent component in <ProductSettingsContextProvider> to fix this error.");
    return context;
}